import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { usePendingShiftSwapApi } from './config/usePendingShiftSwapApi';
import { approveSwap, rejectSwap } from '../../services/shiftSwapService';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import ComplianceDrawer from '../shift-schedules/ComplianceDrawer';

const Container = styled.div``;

const RejectOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const RejectDialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const RejectTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

// Uyum ihlali şiddet renkleri — ShiftSchedulesPage.js ve ComplianceDrawer.js ile
// birebir aynı (backend'in error/warning/info enum'u). Bu iki dosya kendi kopyalarını
// export ETMİYOR ve codebase kasıtlı olarak paylaşılan tek bir export yerine her
// tüketicide yerel kopya tutuyor (bkz. task-10-brief.md) — burada da aynı ikili
// aynen yeniden tanımlanıyor; ortak export'a çıkarmak ayrı, ertelenmiş bir temizlik.
const COMPLIANCE_SEVERITY_COLORS = { error: '#E53935', warning: '#F59E0B', info: '#0291EF' };
const COMPLIANCE_SEVERITY_RANK = { error: 3, warning: 2, info: 1 };

const getHighestComplianceSeverityColor = (list) => {
  if (!list || !list.length) return COMPLIANCE_SEVERITY_COLORS.info;
  let top = list[0];
  list.forEach((v) => {
    if ((COMPLIANCE_SEVERITY_RANK[v.severity] || 0) > (COMPLIANCE_SEVERITY_RANK[top.severity] || 0)) top = v;
  });
  return COMPLIANCE_SEVERITY_COLORS[top.severity] || COMPLIANCE_SEVERITY_COLORS.info;
};

const CompliancePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $color }) => `${$color}55`};
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.75;
  }
`;

const ComplianceCleanPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid rgba(16, 185, 129, 0.35);
  background: rgba(16, 185, 129, 0.08);
  color: #10B981;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`;

const ComplianceDot = styled.span`
  display: block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ComplianceDash = styled.span`
  color: #B6BEC9;
  font-size: 13px;
`;

export default function ShiftSwapApprovalsPage() {
  const { t } = useTranslation();

  const { columns: baseColumns, data, fetching, refresh } = usePendingShiftSwapApi();

  const [approveConfirm, setApproveConfirm] = useState({ isOpen: false, id: null, errorCount: 0 });
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [complianceDrawer, setComplianceDrawer] = useState({ isOpen: false, violations: [] });

  const handleApproveClick = useCallback((row) => {
    setApproveConfirm({ isOpen: true, id: row.id, errorCount: row?.compliancePreview?.errorCount || 0 });
  }, []);

  // No try/catch here on purpose — same reasoning as MyShiftSwapsPage.js's
  // handleAcceptConfirm: the Axios interceptor already toasts mutation errors and
  // rejects the promise, so a rejection here simply means the .then() below never
  // runs and the dialog stays open. `finally` only clears local state.
  const handleApproveConfirm = useCallback(() => {
    setProcessing(true);
    approveSwap(approveConfirm.id, { note: '' })
      .then(() => refresh())
      .finally(() => {
        setProcessing(false);
        setApproveConfirm({ isOpen: false, id: null, errorCount: 0 });
      });
  }, [approveConfirm.id, refresh]);

  const handleRejectOpen = useCallback((row) => {
    setRejectReason('');
    setRejectDialog({ isOpen: true, id: row.id });
  }, []);

  const handleRejectConfirm = useCallback(() => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    rejectSwap(rejectDialog.id, { note: rejectReason })
      .then(() => refresh())
      .finally(() => {
        setProcessing(false);
        setRejectDialog({ isOpen: false, id: null });
      });
  }, [rejectDialog.id, rejectReason, refresh]);

  const handleOpenCompliance = useCallback((row) => {
    setComplianceDrawer({ isOpen: true, violations: row?.compliancePreview?.violations || [] });
  }, []);

  const handleCloseCompliance = useCallback(() => {
    setComplianceDrawer({ isOpen: false, violations: [] });
  }, []);

  // Kuyruktaki her satır zaten backend'in GetPending'i tarafından "bu yöneticinin
  // onay bekleyen takasları" olarak filtrelenmiş geliyor (bkz. task-10-brief.md) —
  // MyShiftSwapsPage.js'in peer accept/reject/cancel'ının aksine rol/duruma göre
  // koşullu görünürlük yok, Onayla/Reddet her satırda aynı şekilde sunulur (Leave'in
  // PendingApprovalsPage.js'i ile aynı yaklaşım).
  const actionOptions = useMemo(() => [
    { type: 'approve', label: t('shiftSwap.action.approve'), onClick: handleApproveClick },
    { type: 'reject', label: t('shiftSwap.action.reject'), onClick: handleRejectOpen },
  ], [t, handleApproveClick, handleRejectOpen]);

  // Uyum önizleme rozeti ve aksiyonlar sütunu SAYFA state'ine (seçili önizleme,
  // onay/red diyalogları) ihtiyaç duyduğu için hook'un baseColumns'ı yerine burada
  // eklenir — PendingApprovalsPage.js'in "actions" sütununu baseColumns'a eklemesiyle
  // aynı ayrım (veri sütunları hook'ta, etkileşimli sütunlar sayfada).
  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'compliancePreview',
      label: t('shiftSwap.approvals.compliance'),
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const preview = row?.compliancePreview;
          // Vardiya silinmiş/değişmiş olabilir — backend bu durumda null döner
          // (bkz. task-10-brief.md); nötr bir tire göster, boş rozet gösterme.
          if (!preview) return <ComplianceDash>—</ComplianceDash>;

          const { errorCount = 0, warningCount = 0, infoCount = 0, violations = [] } = preview;
          if (errorCount + warningCount + infoCount === 0) {
            return <ComplianceCleanPill>{t('shiftSwap.approvals.complianceClean')}</ComplianceCleanPill>;
          }

          const color = getHighestComplianceSeverityColor(violations);
          const parts = [];
          if (errorCount > 0) parts.push(`${errorCount} ${t('shiftCompliance.severity.error')}`);
          if (warningCount > 0) parts.push(`${warningCount} ${t('shiftCompliance.severity.warning')}`);
          if (infoCount > 0) parts.push(`${infoCount} ${t('shiftCompliance.severity.info')}`);

          return (
            <CompliancePill type="button" $color={color} onClick={() => handleOpenCompliance(row)}>
              <ComplianceDot $color={color} />
              {parts.join(' · ')}
            </CompliancePill>
          );
        },
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data, t, handleOpenCompliance]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={approveConfirm.isOpen}
        title={t('shiftSwap.approvals.approveTitle')}
        message={
          t('shiftSwap.approvals.approveMessage')
          + (approveConfirm.errorCount > 0 ? ` ${t('shiftSwap.approvals.complianceWarn')}` : '')
        }
        onConfirm={handleApproveConfirm}
        onCancel={() => setApproveConfirm({ isOpen: false, id: null, errorCount: 0 })}
        confirmText={t('shiftSwap.action.approve')}
        loading={processing}
      />

      {rejectDialog.isOpen && (
        <RejectOverlay onClick={() => setRejectDialog({ isOpen: false, id: null })}>
          <RejectDialog onClick={(e) => e.stopPropagation()}>
            <RejectTitle>{t('shiftSwap.approvals.rejectTitle')}</RejectTitle>
            <TextInput
              label={t('shiftSwap.approvals.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button variant="outline" onClick={() => setRejectDialog({ isOpen: false, id: null })} disabled={processing}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleRejectConfirm} disabled={processing || !rejectReason.trim()}
                style={{ background: '#d32f2f' }}>
                {processing ? '...' : t('shiftSwap.action.reject')}
              </Button>
            </div>
          </RejectDialog>
        </RejectOverlay>
      )}

      <ComplianceDrawer
        isOpen={complianceDrawer.isOpen}
        onClose={handleCloseCompliance}
        violations={complianceDrawer.violations}
      />

      {useMemo(() => (
        <DataTable
          title={t('shiftSwap.approvals.title')}
          serverSide={false}
          fetching={fetching}
          columns={columns}
          data={data}
          pageSize={10}
        />
      ), [t, fetching, columns, data])}
    </Container>
  );
}
